import { isValidElement } from 'react';

/**
 * What a guard renders in place of its children when it is closed: either a component type the guard
 * constructs itself, which is how route wrappers name a page (`fallback={NotFoundPage}`), or an
 * element the caller has already built.
 */
export type GuardFallback = React.ComponentType | React.ReactNode;

/** Resolves a {@link GuardFallback} to renderable output. */
export function renderGuardFallback(fallback?: GuardFallback): React.ReactNode {
  if (fallback == null) return null;

  // Checked before the component cases: an element carries a `$$typeof` tag of its own.
  if (isValidElement(fallback)) return fallback;

  // Text, numbers and booleans are nodes React renders as they are.
  if (typeof fallback !== 'function' && typeof fallback !== 'object') return fallback;

  // So are the remaining node shapes React understands: a list of children, or a promise of them.
  if (typeof fallback === 'object' && (Symbol.iterator in fallback || 'then' in fallback)) return fallback;

  // Everything left is a component type. `memo`, `forwardRef` and `lazy` are typed as callable but
  // return an object at runtime, so they land here rather than in the `function` case and still have
  // to be constructed rather than handed to React as a child.
  const Fallback = fallback;
  return <Fallback />;
}
